import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addCivilCaseResolution,
  editCivilCaseAppeal2,
  editCivilCaseResolution,
} from "services/civilCaseComplaintDetailService";
import {
  editCivilCaseComplaint,
  fetchProsecutor,
} from "services/civilCaseComplaintService";
import { adminOrgStructure } from "services/orgService";
import {
  CivilCaseAppeald,
  CivilCaseComplaint,
  CivilCaseResolutions,
  Roles,
  Structure,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  isEdit?: boolean;
  resolution?: CivilCaseResolutions;
  compliant?: CivilCaseComplaint;
  complaintId?: number | string;
  appeal?: CivilCaseAppeald;
  onClose: (refetch: boolean) => void;
};

const CivilCaseResolutionForm: FC<Props> = ({
  isEdit = false,
  resolution,
  compliant,
  complaintId,
  appeal,
  onClose,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<CivilCaseResolutions>(
    resolution || { decision_type: "further_review" },
  );
  const [loading, setLoading] = useState(false);
  const [attachment, setAttachment] = useState<File | null>();
  const [panelAttachment, setPanelAttachment] = useState<File | null>();
  const [action, setAction] = useState<"On Appointment" | "Resolved">(
    "On Appointment",
  );

  const fetchStructure = async (adminId: string, orgId: string) => {
    const structure = await adminOrgStructure(adminId, orgId);
    setStructure(structure);
  };

  const [structure, setStructure] = useState<Structure>();

  useEffect(() => {
    if (loginData) {
      fetchStructure(
        loginData?.admin_unit.toString(),
        loginData?.org_unit.toString(),
      );
    }
  }, [loginData]);

  const isManagement = hasRole(loginData?.roles, [Roles.management]);
  const isProsecutor = hasRole(loginData?.roles, [
    Roles.ComplaintManagementProsecutor,
  ]);

  const [lowerAdminId, setLowerAdminId] = useState("");
  const [lowerOrgId, setLowerOrgId] = useState("");
  const [panelDecisionFound, setPanelDecisionFound] = useState(false);

  useEffect(() => {
    if (resolution) {
      setFormState(resolution);
      setAction(
        resolution?.decision_type === "further_review"
          ? "On Appointment"
          : "Resolved",
      );
    }
  }, [resolution]);

  useEffect(() => {
    if (isProsecutor) {
      setFormState((prev) => ({ ...prev, decision_type: "resolved" }));
      setAction("Resolved");
    }
  }, [isProsecutor]);

  const [prosecutors, setProsecutors] = useState<
    {
      user: {
        id: string;
        first_name: string;
        last_name: string;
      };
    }[]
  >([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const fetchProsecutorData = (
    adminId: string | number,
    orgId: string | number,
  ) => {
    setLoading(true);
    fetchProsecutor(loginData?.admin_unit ?? "", loginData?.org_unit ?? "")
      .then((data) => {
        setProsecutors(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchProsecutorData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData]);

  useEffect(() => {
    if (compliant?.assgined_prosecutor && prosecutors) {
      setSelectedId(compliant?.user_id_assgined_prosecutor ?? null);
      setSelectedName(compliant?.assgined_prosecutor);
    }
  }, [compliant, prosecutors]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.decision_type && action === "Resolved")
      return "Decision Type is Required.";
    if (!selectedId && formState?.decision_type === "assign_prosecutor")
      return "Prosecutor is Required.";
    if (!formState?.appointment_date && action === "On Appointment")
      return "Appointment Date is Required.";
    if (
      formState.appointment_date &&
      action === "On Appointment" &&
      new Date(formState.appointment_date) < new Date()
    ) {
      return `Appointment Date must be after ${new Date().toLocaleDateString()}`;
    }
    if (
      formState.appointment_date &&
      action === "Resolved" &&
      new Date(formState.appointment_date) > new Date()
    ) {
      return `Resolution Date must be on or before ${new Date().toLocaleDateString()}`;
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction = isEdit
      ? editCivilCaseResolution
      : addCivilCaseResolution;
    const isRegional =
      structure?.admin_unit?.parent_unit === null ? true : false;

    if (compliant && formState?.decision_type === "assign_prosecutor") {
      console.log("if");

      editCivilCaseComplaint({
        id: compliant?.id,
        user_id_assgined_prosecutor: selectedId,
        assgined_prosecutor: selectedName,
        cordinator_name: loginData?.full_name,
        user_id_cordinator: loginData?.user_id,
        status:
          formState?.decision_type === "assign_prosecutor"
            ? "assigned_to_prosecutor"
            : "on_appointment",
      })
        .then(() => {
          showTooltip(
            isEdit ? "Successfully Updated!" : "Successfully Submitted!",
            "success",
          );
          setFormState({});
          navigate(appeal ? "/civil-appeal" : "/civil-case-complaint", {
            replace: true,
          });
        })
        .catch((error) => {
          showTooltip(
            error?.response?.data?.detail || errorHandlerOnForm(error),
            "error",
          );
        })
        .finally(() => setLoading(false));
    } else if (
      compliant &&
      compliant?.status === "assigned_to_prosecutor" &&
      formState?.decision_type === "resolved"
    ) {
      console.log("esle if");

      submitAction({
        ...formState,
        decision_type: undefined,
        complaint: complaintId,
        appealed_complaint_id: appeal?.id,
        appealed_complaint: appeal?.id,
        decision_document: attachment,
        resolved_by: loginData?.full_name,
        resolved_by_user_id: loginData?.user_id,
        s_escalated: true,
        admin_unit_id: loginData?.admin_unit,
        org_unit_id: loginData?.org_unit,
      })
        .then(() => {
          editCivilCaseComplaint({
            id: compliant?.id,
            user_id_assgined_prosecutor: selectedId,
            assgined_prosecutor: selectedName,
            cordinator_name: loginData?.full_name,
            user_id_cordinator: loginData?.user_id,
            // status:  "assigned_to_prosecutor",
          });
          showTooltip(
            isEdit ? "Successfully Updated!" : "Successfully Submitted!",
            "success",
          );
          setFormState({});
          navigate(appeal ? "/civil-appeal" : "/civil-case-complaint", {
            replace: true,
          });
        })
        .catch((error) => {
          showTooltip(
            error?.response?.data?.detail || errorHandlerOnForm(error),
            "error",
          );
        })
        .finally(() => setLoading(false));
    } else {
      console.log("else");
      submitAction({
        ...formState,
        complaint: complaintId,
        appealed_complaint_id: appeal?.id,
        appealed_complaint: appeal?.id,
        decision_document: attachment,
        panel_decision_document: panelAttachment,
        resolved_by: loginData?.full_name,
        resolved_by_user_id: loginData?.user_id,
        admin_unit_id: loginData?.admin_unit,
        org_unit_id: loginData?.org_unit,
      })
        .then(() => {
          const admin_unit_id =
            formState?.decision_type === "forwarded"
              ? lowerAdminId
              : isRegional
                ? loginData?.admin_unit
                : structure?.org_unit?.parent_unit
                  ? loginData?.admin_unit
                  : structure?.admin_unit;

          const org_unit_id =
            formState?.decision_type === "forwarded"
              ? null
              : formState?.decision_type === "resolved"
                ? structure?.org_unit?.parent_unit
                  ? structure?.org_unit?.parent_unit
                  : null
                : formState?.decision_type === "escalate_to_higher_authority"
                  ? structure?.org_unit?.parent_unit
                    ? structure?.org_unit?.parent_unit
                    : null
                  : formState?.decision_type === "forward_to_lower_authority"
                    ? lowerOrgId
                      ? lowerOrgId
                      : loginData?.org_unit
                    : loginData?.org_unit;

          if (compliant) {
            editCivilCaseComplaint({
              id: compliant?.id,
              status:
                formState?.decision_type === "further_review"
                  ? "on_appointment"
                  : formState?.decision_type === "resolved"
                    ? "forwarded_to_registrar"
                    : formState?.decision_type === "close"
                      ? "closed"
                      : "forwarded_to_cordinator",
              admin_unit_id,
              org_unit_id,
            });
          }
          if (appeal) {
            editCivilCaseAppeal2({
              id: appeal?.id,
              status:
                formState?.decision_type === "further_review"
                  ? "on_appointment"
                  : formState?.decision_type === "resolved"
                    ? "forwarded_to_registrar"
                    : formState?.decision_type === "close"
                      ? "closed"
                      : "forwarded_to_management",
              complaint: appeal?.complaint,
              parent_appeal: appeal.parent_appeal_id || appeal?.parent_appeal,
              admin_unit_id,
              org_unit_id,
            });
          }

          showTooltip(
            isEdit ? "Successfully Updated!" : "Successfully Submitted!",
            "success",
          );
          setFormState({});
          navigate(appeal ? "/civil-appeal" : "/civil-case-complaint", {
            replace: true,
          });
        })
        .catch((error) => {
          showTooltip(
            error?.response?.data?.detail || errorHandlerOnForm(error),
            "error",
          );
        })
        .finally(() => setLoading(false));
    }
  };

  const handleFile = (file: File | null) => {
    setAttachment(file);
  };

  return (
    <div className="w-full bg-white rounded-xl">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {action === "Resolved" ? "Add Decision" : "Set Appointment"}
        </h2>
        <p className="text-sm text-gray-600">
          Fill out the form to{" "}
          {action === "Resolved" ? "add a resolution" : "set an appointment"}{" "}
          for this compliant on prosecutor.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Decision Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Decision Type *
            </label>
            <select
              required
              name="decision_type"
              value={formState?.decision_type || "further_review"}
              onChange={(e) => {
                setFormState({
                  ...formState,
                  decision_type: e.target
                    .value as CivilCaseResolutions["decision_type"],
                });
                setAction(
                  e.target.value === "further_review"
                    ? "On Appointment"
                    : "Resolved",
                );
              }}
              className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            >
              {/* {Object.entries(CivilCaseResolutionDecisionType).map(
                ([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ),
              )} */}
              {((appeal &&
                !structure?.org_unit?.parent_unit &&
                structure?.admin_unit?.parent_unit) ||
                structure?.org_unit?.parent_unit) &&
                !isProsecutor && (
                  <option value="escalate_to_higher_authority">
                    Escalate to Higher Authority
                  </option>
                )}
              <option value="resolved">Resolved</option>
              {!isProsecutor && (
                <option value="further_review">Further Review</option>
              )}
              {!appeal &&
                !isProsecutor &&
                (structure?.child_org_units?.tree?.children || []) && (
                  <option value="forward_to_lower_authority">
                    Forward to Lower Authority
                  </option>
                )}
              {!isProsecutor && <option value="forwarded">Transfer</option>}
              {compliant && !isProsecutor && (
                <option value="assign_prosecutor">Assign Prosecutor</option>
              )}
              {/* {!isProsecutor && <option value="on_panel">On Panel</option>} */}
              {!isProsecutor && <option value="close">Close</option>}
            </select>
          </div>

          {/* Prosecutor (conditionally shown) */}
          {isManagement && formState?.decision_type === "assign_prosecutor" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Prosecutor *
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                name="reception_method"
                value={selectedId || ""}
                onChange={(e) => {
                  const selectedProsecutor = prosecutors.find(
                    (p) => p?.user?.id?.toString() === e.target.value,
                  );
                  if (selectedProsecutor) {
                    setSelectedId(
                      selectedProsecutor.user?.id !== undefined &&
                        selectedProsecutor.user?.id !== null
                        ? String(selectedProsecutor.user?.id)
                        : "",
                    );
                    setSelectedName(
                      selectedProsecutor.user?.first_name +
                        " " +
                        selectedProsecutor.user?.last_name,
                    );
                  }
                }}
              >
                <option value="">
                  {loading ? "Loading ..." : "Select Prosecutor"}
                </option>
                {Array.isArray(prosecutors) &&
                  prosecutors.map((p, index) => (
                    <option key={index} value={p.user?.id || ""}>
                      {p?.user?.first_name + " " + p?.user?.last_name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Lower Admin Unit (conditionally shown) */}
          {formState?.decision_type === "forwarded" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Lower Admin Unit *
              </label>
              <select
                value={lowerAdminId}
                onChange={(e) => {
                  setLowerAdminId(e.target.value);
                }}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              >
                <option value="">Select Lower Admin Unit</option>
                {(structure?.child_admin_units?.units || []).map((unit) => (
                  <option key={unit?.uu_id} value={unit?.uu_id}>
                    {unit?.unit_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Lower Org Unit (conditionally shown) */}
          {formState?.decision_type === "forward_to_lower_authority" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Lower Org Unit *
              </label>
              <select
                value={lowerOrgId}
                onChange={(e) => {
                  setLowerOrgId(e.target.value);
                }}
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              >
                <option value="">Select Lower Org Unit</option>
                {(structure?.child_org_units?.tree?.children || []).map(
                  (unit) => (
                    <option key={unit?.uu_id} value={unit?.uu_id}>
                      {unit?.unit_name}
                    </option>
                  ),
                )}
              </select>
            </div>
          )}

          {/* Date Field */}
          {formState?.decision_type !== "assign_prosecutor" && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {action === "Resolved"
                  ? "Resolution Date *"
                  : "Appointment Date *"}
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                name="appointment_date"
                value={
                  formState.appointment_date
                    ? new Date(formState.appointment_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    ...e,
                    target: { ...e.target, name: "appointment_date" },
                  })
                }
              />
            </div>
          )}

          {/* Appointment Reason (conditionally shown) */}
          {action === "On Appointment" &&
            formState?.decision_type !== "assign_prosecutor" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Appointment Reason *
                </label>
                <textarea
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                  name="appointment_reason"
                  value={formState?.appointment_reason || ""}
                  onChange={handleChange}
                  placeholder="Enter the reason for the appointment..."
                />
              </div>
            )}

          {/* Decision Summary (conditionally shown) */}
          {action === "Resolved" &&
            formState?.decision_type !== "assign_prosecutor" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Decision Summary *
                </label>
                <textarea
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                  name="decision_summary"
                  value={formState?.decision_summary || ""}
                  onChange={handleChange}
                  placeholder="Enter the decision summary..."
                />
              </div>
            )}

          {/* File Upload (conditionally shown) */}
          {action === "Resolved" &&
            formState?.decision_type !== "assign_prosecutor" && (
              <div className="space-y-2">
                <FileUpload
                  label="Decision Document"
                  fileTypes="*"
                  info="Click to upload or drag and drop"
                  required={false}
                  onChange={handleFile}
                />
              </div>
            )}

          {compliant &&
            action === "Resolved" &&
            formState?.decision_type === "resolved" && (
              <div className="flex flex-row items-center gap-2">
                <input
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-red-600 cursor-pointer"
                  checked={panelDecisionFound}
                  onChange={(e) => setPanelDecisionFound(e.target.checked)}
                />
                <label
                  className="block text-sm font-medium text-gray-700 cursor-pointer"
                  onClick={() => {
                    setPanelDecisionFound(!panelDecisionFound);
                    setPanelAttachment(null);
                  }}
                >
                  Is there a panel decision?
                </label>
              </div>
            )}

          {panelDecisionFound && (
            <div className="space-y-2">
              <FileUpload
                label="Panel Document"
                fileTypes="*"
                info="Click to upload or drag and drop"
                required={false}
                onChange={(file) => setPanelAttachment(file)}
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              onClose(false);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center"
            disabled={loading || !structure}
          >
            {loading ? (
              <>
                <span className="animate-spin">{LoadingIcon}</span>
                Processing...
              </>
            ) : formState?.decision_type === "assign_prosecutor" ? (
              "Assign"
            ) : action === "Resolved" ? (
              "Submit Resolution"
            ) : (
              "Set Appointment"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CivilCaseResolutionForm;
